import { useState, useEffect } from 'react'
import { useClientes } from '../hooks/useClientes'
import { guardarCliente } from '../db/clientes'
import { formatearPeso } from '../utils/formato'
import ModalDetalleCliente from '../components/fiado/ModalDetalleCliente'
import { useConfig } from '../context/ConfigContext'
import { useNavigate } from 'react-router-dom'
import TourBanner from '../components/layout/TourBanner'

export default function Clientes() {
  const { config } = useConfig()
  const navigate = useNavigate()
  const { clientes, cargando, cargar } = useClientes()
  const [busqueda, setBusqueda] = useState('')
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null)
  const [mostrarForm, setMostrarForm] = useState(false)
  const [nombre, setNombre] = useState('')
  const [telefono, setTelefono] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [exitoCliente, setExitoCliente] = useState(false)

  const filtrados = busqueda
    ? clientes.filter(c =>
        c.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        (c.telefono && c.telefono.includes(busqueda))
      )
    : clientes

  const totalDeuda = clientes.reduce((sum, c) => sum + (c.deuda || 0), 0)
  const conDeuda = clientes.filter(c => (c.deuda || 0) > 0).length

  async function handleGuardar() {
    if (!nombre.trim() || guardando) return
    setGuardando(true)
    await guardarCliente({ nombre: nombre.trim(), telefono: telefono.trim() })
    await cargar()
    setNombre('')
    setTelefono('')
    setGuardando(false)
    setExitoCliente(true)
    setTimeout(() => { setExitoCliente(false); setMostrarForm(false) }, 700)
  }

  // Sincronizar clienteSeleccionado con la lista fresca después de cargar()
  useEffect(() => {
    if (clienteSeleccionado) {
      const actualizado = clientes.find(c => c._id === clienteSeleccionado._id)
      if (actualizado) setClienteSeleccionado(actualizado)
    }
  }, [clientes])

  async function handleActualizado() {
    await cargar()
  }

  if (!config.fiadoHabilitado) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-5 p-8 text-center bg-tienda-bg">
        <div className="w-20 h-20 rounded-3xl bg-tienda-surface border border-tienda-border flex items-center justify-center text-4xl">
          🤝
        </div>
        <div>
          <p className="text-xl font-bold text-tienda-text">Módulo de fiado desactivado</p>
          <p className="text-tienda-muted text-sm mt-2 leading-relaxed">
            Activa el módulo de fiado para registrar ventas a crédito
            y llevar el control de lo que te deben tus clientes.
          </p>
        </div>
        <button
          onClick={() => navigate('/config')}
          className="btn-primary px-8 text-base"
        >
          Activar en Configuración →
        </button>
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col bg-tienda-bg">

      {/* ── CABECERA ── */}
      <div className="px-4 pt-5 pb-3 bg-tienda-surface border-b border-tienda-border flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-tienda-text">Pagos</h1>
            {conDeuda > 0 && (
              <p className="text-tienda-muted text-sm mt-0.5">
                {conDeuda} con deuda · {formatearPeso(totalDeuda)} total
              </p>
            )}
          </div>
          <button
            onClick={() => setMostrarForm(true)}
            className="w-11 h-11 rounded-xl bg-tienda-accent/10 border border-tienda-accent/30 flex items-center justify-center text-tienda-accent text-2xl font-bold active:scale-90"
          >
            +
          </button>
        </div>
        <input
          className="input-field"
          placeholder="Buscar cliente..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
        />
      </div>

      {/* Tour banner — solo primera visita */}
      <TourBanner
        moduleKey="pagos"
        titulo="Gestiona tus clientes de fiado"
        descripcion="Agrega clientes, registra ventas a crédito y lleva el control de lo que te deben. Puedes abonar o saldar deudas desde aquí."
      />

      {/* ── LISTA ── */}
      <div className="flex-1 overflow-y-auto p-4">
        {cargando ? (
          <div className="flex items-center justify-center h-40">
            <div className="w-8 h-8 border-4 border-tienda-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtrados.length === 0 ? (
          <EstadoVacio busqueda={busqueda} onAgregar={() => setMostrarForm(true)} />
        ) : (
          <div className="space-y-2">
            {filtrados.map(c => (
              <button
                key={c._id}
                onClick={() => setClienteSeleccionado(c)}
                className="card w-full flex items-center justify-between px-4 py-3.5 text-left active:scale-[0.98] transition-transform"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-tienda-accent/20 flex items-center justify-center flex-shrink-0">
                    <span className="text-tienda-accent font-bold text-lg">
                      {c.nombre.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-tienda-text font-semibold truncate">{c.nombre}</p>
                    {c.telefono && (
                      <p className="text-tienda-muted text-sm truncate">{c.telefono}</p>
                    )}
                  </div>
                </div>
                {(c.deuda || 0) > 0 ? (
                  <span className="badge-danger ml-3 flex-shrink-0">{formatearPeso(c.deuda)}</span>
                ) : (
                  <span className="badge-success ml-3 flex-shrink-0">Al día</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── MODAL NUEVO CLIENTE ── */}
      {mostrarForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-tienda-surface w-full max-w-lg rounded-t-3xl p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-tienda-text">Nuevo cliente</h2>
              <button
                onClick={() => { setMostrarForm(false); setNombre(''); setTelefono('') }}
                className="w-9 h-9 flex items-center justify-center text-tienda-muted"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              autoFocus
              className="input-field"
              placeholder="Nombre *"
              value={nombre}
              onChange={e => setNombre(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleGuardar()}
            />
            <input
              className="input-field"
              placeholder="Teléfono (10 dígitos)"
              value={telefono}
              onChange={e => setTelefono(e.target.value.replace(/\D/g, '').slice(0, 10))}
              type="tel"
              inputMode="numeric"
              maxLength={10}
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setMostrarForm(false); setNombre(''); setTelefono('') }}
                className="btn-ghost flex-1 text-center"
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardar}
                disabled={!nombre.trim() || guardando || exitoCliente}
                className="flex-1 btn-primary text-center text-base"
                style={{ opacity: !nombre.trim() ? 0.5 : 1 }}
              >
                {guardando ? 'Guardando...' : exitoCliente ? '✓ Guardado' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE ── */}
      {clienteSeleccionado && (
        <ModalDetalleCliente
          cliente={clienteSeleccionado}
          onCerrar={() => setClienteSeleccionado(null)}
          onActualizado={handleActualizado}
        />
      )}
    </div>
  )
}

function EstadoVacio({ busqueda, onAgregar }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16 text-center">
      <div className="w-20 h-20 rounded-3xl bg-tienda-surface border border-tienda-border flex items-center justify-center text-4xl">
        👥
      </div>
      <div>
        <p className="text-xl font-bold text-tienda-text">
          {busqueda ? 'Sin resultados' : 'Sin clientes aún'}
        </p>
        <p className="text-tienda-muted text-sm mt-1">
          {busqueda ? `No hay clientes que coincidan con "${busqueda}"` : 'Agrega tu primer cliente para empezar'}
        </p>
      </div>
      {!busqueda && (
        <button onClick={onAgregar} className="btn-primary px-8 text-base">
          Agregar cliente
        </button>
      )}
    </div>
  )
}
